class AgeGate extends HTMLElement {
  constructor() {
    super();

    this._countryTouched = false;
  }

  connectedCallback() {
    const normalize = (p) => (p || "").replace(/\/+$/, "");

    const inferLocaleRootFromPath = (pathname) => {
      // matches "/fr", "/en", "/en-GB", "/pt-BR" at first segment
      const m = (pathname || "").match(/^\/([a-z]{2}(?:-[A-Z]{2})?)(?=\/)/);
      return m ? `/${m[1]}/` : "/";
    };

    const getLocaleRoot = () => {
      const shopifyRoot =
        (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : "/";

      // If Shopify already provides localized root like "/fr/" -> use it.
      if (shopifyRoot && shopifyRoot !== "/") {
        return shopifyRoot.endsWith("/") ? shopifyRoot : (shopifyRoot + "/");
      }

      // Fallback: infer from current path.
      return inferLocaleRootFromPath(window.location.pathname);
    };

    const joinRoot = (root, path) => {
      const r = (root || "/").endsWith("/") ? (root || "/") : ((root || "/") + "/");
      const p = (path || "").replace(/^\/+/, "");
      return (r + p).replace(/\/{2,}/g, "/");
    };

    this.ROOT = getLocaleRoot();
    this.ageGateUrl = joinRoot(this.ROOT, "pages/age-gate");

    this.localeHome = normalize(this.ROOT) || "/";
    this.normalizePath = normalize;

    this.popup = this.querySelector(".c-modal-popup");
    this.dobInput = this.querySelector("#yearOfBirth");
    this.countrySelect = this.querySelector(".c-modal-popup__action--select");
    this.actionBtn = this.querySelector(".c-modal-popup__action-btn");
    this.errorMsg = this.querySelector(".c-modal-popup__error-message");
    this.rememberMeCheckbox = this.querySelector("#rememberMe");
    this.mainElement = this.querySelector(".c-modal-popup__main");
    this.mainContent = this.querySelector("[data-content-main]");
    this.deniedContent = this.querySelector("#age_gate_access_denied");
    this.changeDetails = this.querySelector("div[data-change-details]");

    this.customerExpired = typeof customer_expired !== "undefined" ? customer_expired : false;
    this.expiredSetting = typeof age_gate_expired !== "undefined" ? age_gate_expired : 1;

    this.setupResponsiveBackground();
    this.init();
    this.setupPolicyLinks();
    this.promotePreferredCountryOption(window.PREFERRED_COUNTRY_CODE);

    requestAnimationFrame(() => {
      this.dobInput?.focus();
    });

    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', () => {
        this._countryTouched = true;
      }, { once: true });
    }

    if (this.changeDetails) {
      this.changeDetails.addEventListener('click', () => {
        this.hideAccessDenied();
      })
    }

    this.autofillCountry();
  }

  setupResponsiveBackground() {
    if (this.mainElement) {
      const mobileBackground = this.mainElement.getAttribute(
        "data-background-mobile"
      );
      const desktopBackground = this.mainElement.getAttribute(
        "data-background-desktop"
      );

      if (mobileBackground) {
        this.mainElement.style.setProperty(
          "--mobile-background-url",
          `url(${mobileBackground})`
        );
      }

      if (desktopBackground) {
        this.mainElement.style.backgroundImage = `url(${desktopBackground})`;
      }
    }
  }

  promotePreferredCountryOption(preferredCode) {
    if (!preferredCode) return;

    const code = String(preferredCode).trim().toUpperCase();
    if (!code) return;

    const select = this.countrySelect;
    if (!select || select.tagName !== "SELECT") return;

    const preferredOption = select.querySelector(`option[value="${code}"]`);
    if (!preferredOption) return;

    const placeholder = select.querySelector('option[value=""]');

    if (placeholder) {
      const afterPlaceholder = placeholder.nextSibling;
      if (afterPlaceholder !== preferredOption) {
        select.insertBefore(preferredOption, afterPlaceholder);
      }
    } else {
      if (select.firstChild !== preferredOption) {
        select.insertBefore(preferredOption, select.firstChild);
      }
    }
  }

  init() {
    this.dobInput.addEventListener("input", () => this.validateDOB());
    [this.dobInput, this.countrySelect].forEach((el) => {
      el.addEventListener("change", () => this.validateForm());
      el.addEventListener("keyup", () => this.validateForm());
    });

    this.actionBtn.addEventListener("click", (e) => this.handleSubmit(e));

    document.addEventListener("keypress", (e) => {
      if (
        !this.actionBtn.classList.contains("u-button-disabled") &&
        e.key === "Enter"
      ) {
        this.actionBtn.click();
        e.preventDefault();
      }
    });

    window.addEventListener("load", () => {
      const sessionData = JSON.parse(
        sessionStorage.getItem("rememberageconsent")
      );
      if (sessionData?.country === "Norway") {
        window.location.href = "https://wineinmoderation.eu/";
      }
    });

    this.initialDisplay();
  }

  validateDOB() {
    const cleanedValue = this.dobInput.value
      .replace(/[^0-9]/g, "")
      .substring(0, 4);

    if (cleanedValue === "") {
      this.dobInput.value = "";
      return;
    }

    const year = parseInt(cleanedValue);
    const currentYear = new Date().getFullYear();

    if (!isNaN(year)) {
      this.dobInput.value = Math.min(year, currentYear);
    }
  }

  validateForm() {
    const yearOfBirth = parseInt(this.dobInput.value);
    const country = this.countrySelect.value;
    const currentYear = new Date().getFullYear();

    if (!yearOfBirth || yearOfBirth.toString().length < 4) {
      this.disableButton();
      return;
    }

    if (country) {
      this.dobInput.classList.remove("error-input");
      this.enableButton();
      this.errorMsg?.classList.add("opacity-0");
    } else {
      this.dobInput.classList.add("error-input");
      this.errorMsg?.classList.remove("opacity-0");
    }
  }

  disableButton() {
    this.actionBtn.setAttribute("disabled", true);
    this.actionBtn.classList.add("u-button-disabled");
  }

  enableButton() {
    this.actionBtn.removeAttribute("disabled");
    this.actionBtn.classList.remove("u-button-disabled");
  }

  handleSubmit(e) {
    e.preventDefault();

    const yearOfBirth = parseInt(this.dobInput.value);
    const currentYear = new Date().getFullYear();
    const age = currentYear - yearOfBirth;
    const minAge = 18;
    const isValidAge = age >= minAge && age <= 130;

    if (!isValidAge) {
      this.showAccessDenied();
      return;
    }

    const country =
      this.countrySelect.options[this.countrySelect.selectedIndex].textContent;
    const rememberMe =
      this.rememberMeCheckbox && this.rememberMeCheckbox.checked;

    const userData = { country: country, value: "true" };
    sessionStorage.setItem("rememberageconsent", JSON.stringify(userData));

    // If remember me is checked or customer expired, store in local storage
    if (rememberMe || this.customerExpired) {
      const expiry = Date.now() + this.expiredSetting * 3600 * 10000;
      localStorage.setItem(
        "customer_validate",
        JSON.stringify({ value: country, expiry })
      );
    }

    if (country === "Norway") {
      window.location.href = "https://wineinmoderation.eu/";
    } else {
      const targetHome = this.localeHome || "/";
      if (this.normalizePath(window.location.pathname) !== this.normalizePath(targetHome)) {
        window.location.replace(targetHome);
      }
    }
  }

  initialDisplay() {
    console.log()
    if (this.isDenied()) {
      if (this.normalizePath(window.location.pathname) !== this.normalizePath(this.ageGateUrl)) {
        window.location.href = this.ageGateUrl;
        return;
      }

      this.showPopup();
      this.showAccessDenied();
      return;
    }

    // Check if the age gate validation exists in localStorage
    const customerValidated = (() => {
      const item = localStorage.getItem("customer_validate");
      if (!item) return false;
      try {
        const data = JSON.parse(item);
        // Verify both expiry value exists and is still valid
        return data && data.expiry && data.expiry > Date.now();
      } catch (e) {
        // If JSON parsing fails, invalidate the storage
        localStorage.removeItem("customer_validate");
        return false;
      }
    })();

    // Check if session storage exists
    const sessionExists = (() => {
      const item = sessionStorage.getItem("rememberageconsent");
      if (!item) return false;
      try {
        return JSON.parse(item) !== null;
      } catch (e) {
        // If JSON parsing fails, invalidate the session
        sessionStorage.removeItem("rememberageconsent");
        return false;
      }
    })();

    // Always show popup if validation doesn't exist
    if (!customerValidated && !sessionExists) {
      this.showPopup();
    } else if (this.customerExpired) {
      // Show popup if customer cookie is expired
      this.showPopup();
      // Ensure session data exists
      if (!sessionExists && customerValidated) {
        try {
          const data = JSON.parse(
            localStorage.getItem("customer_validate")
          ).value;
          sessionStorage.setItem(
            "rememberageconsent",
            JSON.stringify({ country: data, value: "true" })
          );
        } catch (e) {
          // If there's an error, reset storage and show popup
          localStorage.removeItem("customer_validate");
          sessionStorage.removeItem("rememberageconsent");
          this.showPopup();
        }
      }
    } else {
      // No need to show popup, validation exists
      console.log("Age gate validation exists, not showing popup");
    }
  }

  showPopup() {
    if (this.normalizePath(window.location.pathname) !== this.normalizePath(this.ageGateUrl)) {
      window.location.href = this.ageGateUrl;
    } else {
      // Force clear any existing storage to ensure popup shows
      if (!this.customerExpired) {
        localStorage.removeItem("customer_validate");
        sessionStorage.removeItem("rememberageconsent");
      }

      // Display the popup
      this.popup.classList.add("visible");
      document.documentElement.classList.add("modal-open");
    }
  }

  showAccessDenied() {
    if (this.mainContent) this.mainContent.classList.add("invisible");

    if (this.deniedContent) {
      this.deniedContent.classList.remove("invisible");
      this.deniedContent.style.display = "flex";

      this.countDownRedirect();
    }
  }

  hideAccessDenied() {
    if (this.mainContent) {
      this.mainContent.classList.remove('invisible');
    }

    if (this.deniedContent) {
      this.deniedContent.classList.add('invisible');
      this.deniedContent.style.display = 'none';

      this.countDownRedirect({ disabled: true });
    }
  }

  countDownRedirect({ disabled = false } = {}) {
    const START_SECONDS = 15;

    const countdownEl = this.querySelector('countdown-redirect');
    if (!countdownEl) return;

    if (this._countdownTimer) {
      clearInterval(this._countdownTimer);
      this._countdownTimer = null;
    }

    if (disabled) {
      countdownEl.textContent = `${START_SECONDS} seconds`;
      return;
    }

    const redirectUrl = countdownEl.dataset.redirectUrl || '/';
    let remaining = START_SECONDS;

    countdownEl.textContent = `${remaining} seconds`;

    this._countdownTimer = setInterval(() => {
      remaining -= 1;

      const display = Math.max(remaining, 0);
      countdownEl.textContent = `${display} seconds`;

      if (remaining <= 0) {
        clearInterval(this._countdownTimer);
        this._countdownTimer = null;
        window.location.href = redirectUrl;
      }
    }, 1000);
  }

  isDenied() {
    return localStorage.getItem("age_gate_denied") === "true";
  }

  setupPolicyLinks() {
    const policyContainer = this.querySelector(".age-gate__policy");
    if (!policyContainer) return;

    const links = policyContainer.querySelectorAll("a");

    links.forEach(link => {
      link.addEventListener("click", async (e) => {
        e.preventDefault();

        const url = link.href;
        if (!url) return;

        try {
          const response = await fetch(url);
          const htmlString = await response.text();

          const parser = new DOMParser();
          const html = parser.parseFromString(htmlString, "text/html");

          const mainContent = html.querySelector("#MainContent");

          this.renderPolicyPopup(mainContent ? mainContent.innerHTML : "No content found");
        } catch (err) {
          console.error("Failed to load policy page:", err);
        }
      });
    });
  }

  renderPolicyPopup(content) {
    const popup = document.createElement("div");
    popup.className = "age-gate-policy-popup";
    popup.innerHTML = `
      <div class="age-gate-policy-overlay"></div>
      <div class="age-gate-policy-box">
        <div class="policy-header">
          <button class="policy-close">
            <svg class="icon icon-close-20px w-2,5 h-2,5 text-primary-lighter" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5 5L15 15" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="policy-content">${content}</div>
      </div>
    `;

    document.body.appendChild(popup);

    popup.querySelector(".policy-close").onclick = () => popup.remove();
    popup.querySelector(".age-gate-policy-overlay").onclick = () => popup.remove();
  }

  autofillCountry() {
    const selectEl = this.countrySelect;
    if (!selectEl) return;

    if (this._countryTouched) return;

    const currentCountry = (this.dataset.currentCountry || '').trim().toUpperCase();
    if (!currentCountry) return;

    const match = Array.from(selectEl.options).find(
      (o) => String(o.value).trim().toUpperCase() === currentCountry
    );
    if (!match) return;

    selectEl.value = match.value;

    selectEl.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define("age-gate", AgeGate);
